import React, { useState, useEffect } from 'react';
import DashboardReportService from '@services/DashboardReportService';
import { ReportSelect } from '../ReportSelect';

import '../ReportViewer.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Layer {
  LayerId: number;
  Name: string;
}

interface DateField {
  CustomFieldId: number;
  Name: string;
}

interface CardType {
  ConfigValueID: number;
  Name: string;
}

interface Dashboard {
  ReportId: number;
  DashboardId: number;
  ConfigValueId?: number;
  ProjectId?: number;
}

interface Config {
  apiUrl: string;
  authToken: string;
  timeZone: string;
}

interface SaveRequest {
  ReportId: number;
  WidgetId: number;
  LayerId: number;
  DashboardId: number;
  DateStartFieldId: number;
  DateStartIds: unknown;
  DateEndFieldId: number;
  DateEndIds: unknown;
  AddDynamicGroup: boolean;
  DueDate: unknown;
  StartDate: unknown;
  TypePeriodSelected: unknown;
  CustomTeamSize: number;
  CardType: string;
  ConfigValueID: number;
}

interface Props {
  dashboard: Dashboard;
  config?: Config;
  settings: Record<string, unknown>;
  widgetId: number;
  LayerId: number | string;
  startDate: unknown;
  dueDate: unknown;
  onSave: (request: SaveRequest, layerId: number) => void;
  onCancel: () => void;
}

const getFinalValues = (selected: number, allOthers: (DateField | DateField[])[]) =>
{
  if (allOthers.length === 0)
  {
    return false;
  }

  let result = '';
  const takeFrom: (DateField | DateField[])[] = [];

  for (let i = 0; i < allOthers.length; i++)
  {
    const item = allOthers[i];
    // If multiple fields with same name, the response from server is an array
    if (Array.isArray(item))
    {
      if (item[0]!.CustomFieldId === selected)
      {
        takeFrom.push(item);
        break;
      }
    }
    else
    {
      // no repeated fields. No need for a sub-index.
      if (item!.CustomFieldId === selected)
      {
        takeFrom.push(item!);
        break;
      }
    }
  }

  if (takeFrom.length === 0) return false;

  const first = takeFrom[0]!;
  if (!Array.isArray(first))
  {
    result = result + first.CustomFieldId.toString();
    result = result + ',';
  }
  else
  {
    for (let j = 0; j < first.length; j++)
    {
      result = result + first[j]!.CustomFieldId.toString();
      if (j < first.length - 1)
      {
        result = result + ',';
      }
    }
  }
  return result;
};

// ─── Component ────────────────────────────────────────────────────────────────

const BurnDownChartFinishedSettings: React.FC<Props> = ({
  dashboard,
  config: _config,
  settings,
  widgetId,
  LayerId,
  startDate,
  dueDate,
  onSave,
  onCancel,
}) =>
{
  // Label/value keys for Select components
  const VALUE_FIELD = 'Name' as const;
  const CUSTOM_FIELD_ID = 'CustomFieldId' as const;

  // ─── State ──────────────────────────────────────────────────────────────────

  const [_layers, setLayers] = useState<Layer[]>([{ LayerId: 0, Name: 'Loading...' }]);
  const [allDateFields, setAllDateFields] = useState<(DateField | DateField[])[]>([]);
  const [dateFields, setDateFields] = useState<DateField[]>([
    { CustomFieldId: 0, Name: 'Loading...' },
  ]);
  const [cardTypes, setCardTypes] = useState<CardType[]>([]);

  const [selectedLayer, setSelectedLayer] = useState<number>(parseInt(String(LayerId)));
  const [dateStart, setDateStart] = useState<number>(
    parseInt(String(settings.DateStartFieldId ?? 0)),
  );
  const [dateEnd, setDateEnd] = useState<number>(parseInt(String(settings.DateEndFieldId ?? 0)));
  const [addDynamicGroup, setAddDynamicGroup] = useState<boolean>(
    settings.AddDynamicGroup !== undefined ? JSON.parse(String(settings.AddDynamicGroup)) : true,
  );
  const [customTeamSize, setCustomTeamSize] = useState<string>(
    settings.CustomTeamSize !== undefined
      ? String(JSON.parse(String(settings.CustomTeamSize)))
      : '0',
  );
  const [cardTypesSelected, setCardTypesSelected] = useState<CardType>({
    ConfigValueID: settings.ConfigValueID ? parseInt(String(settings.ConfigValueID)) : 0,
    Name: settings.CardType ? String(settings.CardType) : 'All Card Types',
  });
  const [fieldsValid, setFieldsValid] = useState<boolean>(true);
  const [typePeriodSelected, _setTypePeriodSelected] = useState<unknown>(undefined);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  // ─── Data loading ────────────────────────────────────────────────────────────

  const loadDataFields = (layerDefault?: number) =>
  {
    const layerSelectedFilter = layerDefault !== undefined ? layerDefault : selectedLayer;

    const requestCustomFields = {
      // api: apiConfig.current.api,
      LayerId: layerSelectedFilter,
      TypeId: 3,
      configValueId: dashboard.ConfigValueId ?? 1,
    };

    if (layerSelectedFilter <= 0) return;

    if (layerDefault !== undefined)
    {
      setDateFields([{ CustomFieldId: 0, Name: 'Loading...' }]);
    }

    setTimeout(() =>
    {
      requestCustomFields.TypeId = 6;

      DashboardReportService.getCustomFieldsByLayerAndId(requestCustomFields)

        .then(((data: DateField[] | DateField[][]) =>
        {
          let resolvedDateFields: DateField[] = [];

          if ((dashboard.ConfigValueId ?? 1) === 1)
          {
            for (const arrValues of data as DateField[][])
            {
              if (Array.isArray(arrValues))
              {
                const sorted = [...arrValues].sort((a, b) => a.CustomFieldId - b.CustomFieldId);
                resolvedDateFields.push(sorted[0]!);
              }
            }
          }
          else
          {
            resolvedDateFields = data as DateField[];
          }

          // Determine start date
          let _dateStart = dateStart;
          let _dateEnd = dateEnd;

          const dateSPreselected = resolvedDateFields.filter(
            obj => obj.CustomFieldId === _dateStart,
          );
          if (!dateSPreselected.length)
          {
            const startDateField = resolvedDateFields.find(obj => obj.Name === 'Start date');
            _dateStart = startDateField
              ? startDateField.CustomFieldId
              : (resolvedDateFields[0]?.CustomFieldId ?? _dateStart);
          }

          // Determine end date
          const dateEPreselected = resolvedDateFields.filter(obj => obj.CustomFieldId === _dateEnd);
          if (!dateEPreselected.length)
          {
            const endDateField = resolvedDateFields.find(obj => obj.Name === 'Due date');
            _dateEnd = endDateField
              ? endDateField.CustomFieldId
              : (resolvedDateFields[0]?.CustomFieldId ?? _dateEnd);
          }

          setAllDateFields(data as (DateField | DateField[])[]);
          setDateFields(resolvedDateFields);
          setDateStart(_dateStart);
          setDateEnd(_dateEnd);

          // Load card types
          DashboardReportService.getMenuByConfigValuePerson(dashboard.ProjectId ?? 0)

            .then(((response: { ConfigValueID: number; Text: string }[]) =>
            {
              const subMenuItems = response
                .filter(x => ![1, 9, 15].includes(x.ConfigValueID))
                .map(cardVal => ({
                  ConfigValueID: cardVal.ConfigValueID,
                  Name: cardVal.Text,
                }));

              subMenuItems.unshift({ Name: 'All Card Types', ConfigValueID: 0 });
              setCardTypes(subMenuItems);
            }) as any)
            .catch((error: unknown) =>
            {
              console.error(error);
            });
        }) as any);
    });
  };

  // ─── Effects ─────────────────────────────────────────────────────────────────

  useEffect(() =>
  {
    DashboardReportService.getPersonLayers().then((data: Layer[]) =>
    {
      setLayers(data);
      setSelectedLayer(parseInt(String(LayerId)));
    });

    loadDataFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const onChangeSelect = (e: DateField | null): void =>
  {
    setDateStart(e ? e.CustomFieldId : 0);
    setFieldsValid(!!e);
  };

  const onChangeSelectDueDate = (e: DateField | null): void =>
  {
    setDateEnd(e ? e.CustomFieldId : 0);
    setFieldsValid(!!e);
  };

  const onChangeSelectCardType = (e: CardType | null): void =>
  {
    if (e) setCardTypesSelected(e);
  };

  const onChangeAddDynamicGroup = (): void =>
  {
    setAddDynamicGroup(prev => !prev);
  };

  const onChangeCustomTeamSize = (e: React.ChangeEvent<HTMLInputElement>): void =>
  {
    const newValue = e.target.value.replace(/[^0-9.]|(\..*\.)/g, '');
    setCustomTeamSize(newValue);
  };

  const saveSettings = async (): Promise<void> =>
  {
    if (selectedLayer !== 0 && dateStart && dateStart !== 0 && dateEnd && dateEnd !== 0)
    {
      const EndDateFieldId = getFinalValues(dateEnd, allDateFields);
      const StartDateFieldId = getFinalValues(dateStart, allDateFields);

      const requestSave = {
        ReportId: dashboard.ReportId,
        WidgetId: widgetId,
        LayerId: selectedLayer,
        DashboardId: dashboard.DashboardId,
        DateStartFieldId: dateStart,
        DateStartIds: StartDateFieldId ?? settings.DateStartIds,
        DateEndFieldId: dateEnd,
        DateEndIds: EndDateFieldId ?? settings.DateEndIds,
        AddDynamicGroup: addDynamicGroup,
        DueDate: dueDate,
        StartDate: startDate,
        TypePeriodSelected: typePeriodSelected,
        CustomTeamSize:
          customTeamSize.length > 0 && customTeamSize !== '.'
            ? parseFloat(parseFloat(customTeamSize).toFixed(1))
            : 0,
        CardType: cardTypesSelected.Name,
        ConfigValueID: cardTypesSelected.ConfigValueID,
      };

      await DashboardReportService.updateWidgetBurnDownChartFinishedSettings(requestSave);
      onSave(requestSave, selectedLayer);
      onCancel();
    }
    else
    {
      setFieldsValid(false);
    }
  };

  // ─── Derived values for Select ────────────────────────────────────────────────

  const dateStartValue = dateFields.find(d => d.CustomFieldId === dateStart) ?? null;
  const dateEndValue = dateFields.find(d => d.CustomFieldId === dateEnd) ?? null;

  const isDark = document.documentElement.classList.contains('dark');

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      <h4
        className="widget-setting-heading type-A"
        style={{ color: isDark ? '#a9a093' : 'inherit' }}
      >
        Widget Settings
      </h4>
      <div className="widget-setting-section-burndown">
        {/* Empty date row kept for layout parity with original */}
        <div className="widget-setting-rowSBurndown widget-setting-Dates" />

        {/* Card Types */}
        <div className="widget-setting-rowSBurndown">
          <div className="widget-setting-label">
            <span>Card Types:</span>
          </div>
          <div className="widget-setting-value widget-setting-startDate widget-setting-dueDate">
            <ReportSelect
              autoFocus={false}
              options={cardTypes}
              onChange={onChangeSelectCardType}
              value={cardTypesSelected}
              isMulti={false}
              isSearchable={true}
              openMenuOnFocus={true}
              closeMenuOnSelect={true}
              getOptionLabel={o => o.Name}
              getOptionValue={o => String(o.ConfigValueID)}
              styles={{
                container: base => ({ ...base, width: 150 }),
                control: base => ({ ...base, minHeight: 20 }),
                menu: base => ({ ...base, width: 150 }),
              }}
            />
          </div>
        </div>

        {/* Start Date Field */}
        <div className="widget-setting-rowSBurndown">
          <div className="widget-setting-label">
            <span>Start Date Field:</span>
          </div>
          <div className="widget-setting-value widget-setting-startDate widget-setting-dueDate">
            <ReportSelect
              autoFocus={false}
              options={dateFields}
              onChange={onChangeSelect}
              value={dateStartValue}
              isMulti={false}
              isSearchable={true}
              openMenuOnFocus={true}
              closeMenuOnSelect={true}
              getOptionLabel={o => o[VALUE_FIELD]}
              getOptionValue={o => String(o[CUSTOM_FIELD_ID])}
              styles={{
                container: base => ({ ...base, width: 150 }),
                control: base => ({ ...base, minHeight: 20 }),
                menu: base => ({ ...base, width: 150 }),
              }}
            />
          </div>
        </div>

        {/* Due Date Field */}
        <div className="widget-setting-rowSBurndown">
          <div className="widget-setting-label">
            <span>Due Date Field:</span>
          </div>
          <div className="widget-setting-value widget-setting-startDate widget-setting-dueDate">
            <ReportSelect
              autoFocus={false}
              options={dateFields}
              onChange={onChangeSelectDueDate}
              value={dateEndValue}
              isMulti={false}
              isSearchable={true}
              openMenuOnFocus={true}
              closeMenuOnSelect={true}
              getOptionLabel={o => o[VALUE_FIELD]}
              getOptionValue={o => String(o[CUSTOM_FIELD_ID])}
              styles={{
                container: base => ({ ...base, width: 150 }),
                control: base => ({ ...base, minHeight: 20 }),
                menu: base => ({ ...base, width: 150 }),
              }}
            />
          </div>
        </div>

        {/* Include Dynamic Column */}
        <div className="widget-setting-rowSBurndown">
          <div className="widget-setting-label">
            <label>Include Dynamic Column</label>
          </div>
          <div className="widget-setting-label">
            <span className="custom-checkbox task-card-checkbox">
              <input
                id={`CheckboxChangeAddDynamicGroup${widgetId}`}
                className="taskCardCheckbox"
                type="checkbox"
                checked={addDynamicGroup}
                onChange={onChangeAddDynamicGroup}
              />
              <label
                htmlFor={`CheckboxChangeAddDynamicGroup${widgetId}`}
                className="conversationCheckbox"
              />
            </span>
          </div>
        </div>

        {/* Custom People Assigned */}
        <div className="widget-setting-rowSBurndown">
          <div className="widget-setting-label">
            <label>Custom People Assigned</label>
          </div>
          <div className="widget-setting-value">
            <span>
              <input
                id="NumericCustomTeamSize"
                type="number"
                step="1"
                value={customTeamSize}
                onChange={onChangeCustomTeamSize}
              />
            </span>
          </div>
        </div>

        {/* Validation error */}
        {!fieldsValid && <div style={{ color: 'red' }}>Some fields selected are not valid</div>}

        {/* Actions */}

        <div className="widget-setting-actions flex justify-end gap-2">
          <button
            style={{
              fontSize: 12,
              padding: '4px 14px',
              border: `1px solid ${isDark ? '#4a4c4c' : '#ccc'}`,
              borderRadius: 4,
              background: isDark ? '#3a3c3c' : '#fff',
              color: isDark ? '#a9a093' : '#555',
              cursor: 'pointer',
            }}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            style={{
              fontSize: 12,
              padding: '4px 14px',
              border: 'none',
              borderRadius: 4,
              background: '#62accb',
              color: '#fff',
              cursor: 'pointer',
            }}
            onClick={saveSettings}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default BurnDownChartFinishedSettings;
